import { z } from 'zod';
import {
  DashboardEmailExclusionAddress,
  DashboardEmailExclusionListResponse,
  type DashboardEmail,
  type DashboardEmailInlineContentResponse,
  type DashboardEmailResponse,
  type DismissEmailRequest,
  type DismissEmailResponse,
  type EmailParticipant,
  type OrphanEmailContactOption,
  type UpdateRelationshipRequest,
} from '@apparatix/schemas/DashboardEmails';
import { useContacts } from './useContacts';
import { useEndpoint } from './useEndpoint';
import { useUtilities } from './useUtilities';
import { useToastNotifications } from './useToastNotifications';
import { useStringFunctions } from './useStringFunctions';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';
import { newContactPayloadTransformer, WizardNewContact, wizardNewContact } from '@apparatix/schemas/CreateLeadWizard';
import { useCrmRelationshipsStore } from '@apparatix/stores/useCrmRelationshipsStore';
import { AppStore } from '@apparatix/stores/AppStore';
import type { ContactPayload, CreatedContact } from '@apparatix/types/Contacts';
import type { Relationship } from '@apparatix/schemas/Relationship';
import type { NylasContact } from '@apparatix/types/nylas';


export const useDashboardEmails = () => {
  const { createContact, getFullName } = useContacts();
  const { isEmptyString } = useStringFunctions();
  const { showError, showSuccess } = useToastNotifications();

  const {
    arrayHasValues,
    developerWarning,
    isObject,
    swapArrayPositions,
  } = useUtilities();

  const relationshipStore = useCrmRelationshipsStore();

  const getAllEndpoint = useEndpoint<DashboardEmailResponse>({
    type: 'GET',
    url: '/dashboard/emails',
    successField: 'emails',
    toast: {
      showOnSuccess: false,
    },
  });

  const updateRelationshipEndpoint = useEndpoint<DashboardEmailResponse>({
    type: 'PUT',
    successField: 'emails',
    toast: {
      showOnSuccess: false,
    },
  });

  const dismissEndpoint = useEndpoint<DismissEmailResponse>({
    type: 'POST',
    successField: 'status',
    toast: {
      showOnSuccess: false,
    },
  });

  const importContactEndpoint = useEndpoint<{ data: any }>({
    type: 'POST',
    successField: 'data',
    url: '/dashboard/emails/import',
    toast: {
      showOnSuccess: false,
    },
  });

  const quickCreateContactEndpoint = useEndpoint<{ contact_id: number, activity_id: number }>({
    type: 'POST',
    successField: 'contact_id',
    toast: {
      showOnSuccess: false,
    },
  });

  const inlineContentEndpoint = useEndpoint<DashboardEmailInlineContentResponse>({
    type: 'GET',
    successField: 'success',
    toast: {
      showOnError: false,
      showOnSuccess: false,
    },
  });

  const getAllExclusionsEndpoint = useEndpoint<DashboardEmailExclusionListResponse>({
    type: 'GET',
    successField: 'success',
    url: '/dashboard/emails/exclusions',
    toast: {
      showOnSuccess: false,
    },
  });

  const saveExclusionsEndpoint = useEndpoint<DashboardEmailExclusionListResponse>({
    type: 'POST',
    successField: 'success',
    url: '/dashboard/emails/exclusions',
    toast: {
      showOnSuccess: false,
    },
  });

  const deleteExclusionsEndpoint = useEndpoint<DashboardEmailExclusionListResponse>({
    // POST .../remove rather than DELETE-with-body — DELETE bodies are not reliable
    // through some proxies / clients (see BE route definition).
    type: 'POST',
    successField: 'success',
    url: '/dashboard/emails/exclusions/remove',
    toast: {
      showOnSuccess: false,
    },
  });

  const getParticipantDisplay = (email: DashboardEmail) => {
    const participant = getRelevantParticipant(email);
    if (!participant) {
      return '';
    }

    if (participant.name === participant.email) {
      return participant.email;
    }

    return `${participant.name} (${participant.email})`;
  };

  const emailHasSuggestedContacts = (email: DashboardEmail) => arrayHasValues(email?.relationships?.contacts);

  /**
   * Returns either the to or from participant, depending on whether the direction of the mail is incoming or outgoing
   */
  const getRelevantParticipant = (email: DashboardEmail): EmailParticipant => {
    if (!isObject(email?.participants) || !email?.direction) {
      return undefined;
    }

    const participantField: keyof DashboardEmail['participants'] = email.direction === 'outgoing' ? 'to' : 'from';
    if (!(participantField in email.participants) || !arrayHasValues(email.participants[participantField])) {
      return undefined;
    }

    return email.participants[participantField][0];
  };

  const getParticipantNameOrEmail = (participant: EmailParticipant) => {
    if (isEmptyString(participant.name, true) || participant.name === participant.email) {
      return participant.email;
    }

    return participant.name;
  };

  const getFirstParticipantFieldWithValue = (...args: [DashboardEmail, ...Array<keyof DashboardEmail['participants']>]): keyof DashboardEmail['participants'] => {
    const [email, ...fields] = args;

    if (!isObject(email?.participants)) return undefined;

    for (const field of fields) {
      if (arrayHasValues(email.participants[field])) {
        return field;
      }
    }

    return undefined;
  };

  const placeCurrentUserAtStartOfParticipants = (participants: EmailParticipant[]): EmailParticipant[] => {
    if (isEmptyString(AppStore.currentUser?.externalLinkedEmail, true)) {
      return participants;
    }

    const idx = participants.findIndex(p => p.email === AppStore.currentUser.externalLinkedEmail);

    // atypical check here since idx 0 would mean user is already at start, so we'd do the same thing for 0 and -1
    if (idx <= 0) {
      return participants;
    }

    const copy = [...participants];
    swapArrayPositions(copy, idx, 0);

    return copy;
  };

  const getNewContactVmodel = (email: DashboardEmail) => {
    const participant = getRelevantParticipant(email);

    if (!participant) {
      return createObjectFromSchema(wizardNewContact, {});
    }

    const fields: Partial<WizardNewContact> = {
      emails: [{ addressType: 'work', email: participant.email }],
    };

    if (participant.name !== participant.email && !isEmptyString(participant.name)) {
      fields.fullName = participant.name;
    }

    return createObjectFromSchema(wizardNewContact, fields);
  };

  const updateRelationship = async (emailId: number, relationships: Relationship[]): Promise<DashboardEmailResponse> => {
    updateRelationshipEndpoint.setEndpointUrl(`/dashboard/emails/${emailId}/relationships`);

    const payload: UpdateRelationshipRequest = { relationships };

    return await updateRelationshipEndpoint.getResults(payload);
  };

  const attachRelationshipsToSelectedContact = async (contact: WizardNewContact): Promise<OrphanEmailContactOption> => {
    const relationships = await relationshipStore.getRelationships('contacts', contact.id);

    return {
      id: contact.id,
      name: getFullName(contact),
      companyName: !isEmptyString(contact.companyName, true) ? contact.companyName : undefined,
      accounts: relationships?.Account ?? [],
      opportunities: [
        ...(relationships?.Lead ?? []),
        ...(relationships?.Opportunity ?? []),
      ],
    };
  };

  const handleContactFormSubmit = async (contact: WizardNewContact): Promise<CreatedContact> => {
    try {
      contact.owner = AppStore.currentUser;
      contact.ownerId = AppStore.currentUser.id;

      const payload = newContactPayloadTransformer.parse(contact) as ContactPayload;
      const response = await createContact(payload);

      if (response?.id) {
        return response.contact;
      }
    } catch (e) {
      developerWarning(true, e, 'throw');
      showError('Unable to save Contact. Please try again, or contact support if the problem persists', 'Error');
    }

    return undefined;
  };

  const dismissOrphanNotification = async (email: DashboardEmail, ignoreFuture: boolean = false): Promise<boolean> => {
    dismissEndpoint.setEndpointUrl(`/dashboard/emails/${email.id}/dismiss`);
    const payload: DismissEmailRequest = { ignoreFuture };

    const response = await dismissEndpoint.getResults(payload);

    return !isEmptyString(response?.status, true);
  };

  /**
   * Fetch one page of the dashboard email list. Pass the cursor returned by the
   * previous call to advance; pass nothing (or null) to load the first page.
   */
  const getDashboardEmails = async (
    cursor: string | null = null,
  ): Promise<{ emails: DashboardEmail[], nextCursor: string | null }> => {
    const url = cursor !== null && cursor !== ''
      ? `/dashboard/emails?cursor=${encodeURIComponent(cursor)}`
      : '/dashboard/emails';

    getAllEndpoint.setEndpointUrl(url);
    const response = await getAllEndpoint.getResults();

    return {
      emails: arrayHasValues(response?.emails) ? response.emails : [],
      nextCursor: response?.nextCursor ?? null,
    };
  };

  const getInlineContent = async (emailId: number): Promise<DashboardEmailInlineContentResponse | null> => {
    inlineContentEndpoint.setEndpointUrl(`/dashboard/emails/${emailId}/inline-content`);

    return await inlineContentEndpoint.getResults();
  };

  const importNylasContacts = async (contacts: NylasContact[]): Promise<boolean> => {
    // getResults never throws — it resolves null on failure
    const response = await importContactEndpoint.getResults({ contacts });

    if (response === null) {
      showError('There was an error importing your contacts. Please try again, or contact support');

      return false;
    }

    return true;
  };

  const createContactFromOrphanEmail = async (email: DashboardEmail, showSuccessToast: boolean = true) => {
    quickCreateContactEndpoint.setEndpointUrl(`/dashboard/emails/${email.id}/contact`);

    try {
      const result = await quickCreateContactEndpoint.getResults();

      const success = Boolean(result?.contact_id);

      if (success && showSuccessToast) {
        const participant = getRelevantParticipant(email);
        const contactName = !isEmptyString(participant.name, true) ? participant.name : participant.email;

        showSuccess(`${contactName} successfully created as new Contact`);

        return true;
      }

      return false;
    } catch {
      showError('There was an error creating this contact. Please try again, or contact support');

      return false;
    }
  };

  const getExcludedEmails = async (): Promise<DashboardEmailExclusionAddress[]> => {
    try {
      const response = await getAllExclusionsEndpoint.getResults();

      if (arrayHasValues(response?.emails)) {
        return response.emails;
      }

      return [];
    } catch {
      return [];
    }
  };

  const isValidEmailAddress = (test: string) => {
    try {
      z.string().email().parse(test);
      return true;
    } catch {
      return false;
    }
  };

  const parseCommaSeparatedEmails = (rawInput: string): [validAddresses: string[], invalidInputs: string[]] => {
    const parsedInput = rawInput.split(/[,\r\n]+/).map(line => line.trim());

    return [
      parsedInput.filter(line => isValidEmailAddress(line)),
      parsedInput.filter(line => !isValidEmailAddress(line) && !isEmptyString(line, true)),
    ];
  };

  type SaveExcludedAddressesResult = {
    success: boolean,
    discardedLines?: string[],
  };

  const removeExcludedAddresses = async (values: string[]): Promise<boolean> => {
    try {
      const response = await deleteExclusionsEndpoint.getResults({ values });

      return response?.success ?? false;
    } catch {
      return false;
    }
  };

  const saveExcludedAddresses = async (rawInput: string): Promise<SaveExcludedAddressesResult> => {
    if (isEmptyString(rawInput)) {
      return { success: true };
    }

    const [values, discardedLines] = parseCommaSeparatedEmails(rawInput);

    if (!arrayHasValues(values)) {
      return { success: true, discardedLines };
    }

    try {
      const response = await saveExclusionsEndpoint.getResults({ values });

      if (response?.success === undefined) {
        return { success: false };
      }

      const result: SaveExcludedAddressesResult = { success: true };

      if (arrayHasValues(discardedLines)) {
        result.discardedLines = discardedLines;
      }

      return result;
    } catch {
      return { success: false };
    }
  };

  return {
    attachRelationshipsToSelectedContact,
    createContactFromOrphanEmail,
    dismissOrphanNotification,
    emailHasSuggestedContacts,
    getDashboardEmails,
    getExcludedEmails,
    getFirstParticipantFieldWithValue,
    getInlineContent,
    getNewContactVmodel,
    getParticipantDisplay,
    getParticipantNameOrEmail,
    getRelevantParticipant,
    handleContactFormSubmit,
    importNylasContacts,
    placeCurrentUserAtStartOfParticipants,
    removeExcludedAddresses,
    saveExcludedAddresses,
    updateRelationship,
  };
};
